import { TenantFormattingService } from '../../../core/services/tenant-formatting.service';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faComments, faFile, faClipboardList, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppNotification, CommunicationService, NotificationPage } from '../../../core/services/communication.service';
import { FilesService } from '../../../core/services/files.service';
import { getApiErrorMessage } from '../../../core/utils/api-error-message';
import { CommunicationKind, communicationKinds, notificationOrderQueryParams } from '../../../core/models/communication.model';
import { communicationLimits } from '../../../core/config/communication.config';
@Component({ selector: 'app-notifications', imports: [RouterLink, AppDatePipe, MatPaginatorModule, FontAwesomeModule], templateUrl: './notifications.component.html' })
export class NotificationsComponent implements OnInit, OnDestroy {
  readonly orderQueryParams = notificationOrderQueryParams;
  readonly formatting = inject(TenantFormattingService);
  data?: NotificationPage;
  readonly checkIcon = faCheck;
  readonly pageSize = communicationLimits.notificationPageSize;
  icon(kind: CommunicationKind) {
    return kind === communicationKinds.message ? faComments : kind === communicationKinds.file ? faFile : faClipboardList;
  }
  page = 1;
  unreadOnly = false;
  loading = false;
  markingAll = false;
  error = '';
  private request?: Subscription;
  private subs = new Subscription();
  private destroyed = false;
  private refreshQueued = false;
  constructor(public communication: CommunicationService, private files: FilesService) { }
  ngOnInit(): void {
    this.load();
    this.subs.add(this.communication.changes.subscribe(() => {
      if (this.loading)
        this.refreshQueued = true;
      else
        this.load();
    }));
  }
  ngOnDestroy(): void { this.destroyed = true; this.request?.unsubscribe(); this.subs.unsubscribe(); }
  load(): void {
    if (this.destroyed)
      return;
    this.request?.unsubscribe();
    this.refreshQueued = false;
    this.loading = true;
    this.error = '';
    const request = new Subscription();
    this.request = request;
    request.add(this.communication.inbox(this.page, this.unreadOnly).subscribe({
      next: data => {
        // A change received during this request invalidates its snapshot and counts.
        if (!this.refreshQueued) {
          this.data = data;
          this.page = data.currentPage;
          this.communication.updateUnreadCounts(data);
        }
        this.finishLoad();
      },
      error: err => {
        this.error = getApiErrorMessage(err, 'Известувањата не може да се вчитаат.');
        this.finishLoad();
      }
    }));
  }
  private finishLoad(): void {
    this.loading = false;
    if (this.refreshQueued)
      this.load();
  }
  filter(unread: boolean): void { this.unreadOnly = unread; this.page = 1; this.load(); }
  onPageChange(event: PageEvent): void { this.page = event.pageIndex + 1; this.load(); }
  async markAllRead(): Promise<void> {
    if (this.markingAll) return;
    this.markingAll = true;
    this.error = '';
    try {
      await firstValueFrom(this.communication.readAll());
      this.communication.refreshUnread();
      if (!this.destroyed) this.load();
    } catch (err) {
      if (!this.destroyed) this.error = getApiErrorMessage(err, 'Обидете се повторно.');
    } finally {
      this.markingAll = false;
    }
  }
  markRead(item: AppNotification): void {
    const ids = [item.id];
    // Following an item immediately destroys this component. Let its read receipt finish
    // so order/file links also clear unread state, even when no chat is opened afterward.
    void firstValueFrom(this.communication.read(ids)).then(() => {
      this.communication.refreshUnread();
      if (!this.destroyed)
        this.load();
    }).catch(err => {
      if (!this.destroyed)
        this.error = getApiErrorMessage(err, 'Обидете се повторно.');
    });
  }
  download(item: AppNotification): void {
    if (!item.fileId)
      return;
    this.subs.add(this.files.requestDownloadTicket(item.fileId).subscribe({
      next: result => { this.files.startBrowserDownload(result.ticket); this.markRead(item); },
      error: err => { this.error = getApiErrorMessage(err, 'Датотеката не е достапна.'); }
    }));
  }
  label(kind: CommunicationKind): string {
    return kind === communicationKinds.message ? 'Порака' : kind === communicationKinds.file ? 'Датотека' : 'Налог';
  }
}
